/**
 * Industries and their categories (e.g. business profile, filters, campaign setup).
 * Single source of truth — derive flat lists from this if needed.
 *
 * Niche landing mappings (subset of industry + category pairs) live in nicheIndustryMap.js.
 *
 * Ordered for local low–mid level businesses first; broader / enterprise categories
 * are kept but listed later within each industry.
 */
export const INDUSTRY_CATEGORY_LIST = [
  {
    industry: "Beauty & Wellness",
    categories: [
      "Salons & Beauty Parlours",
      "Makeup Artists",
      "Bridal Makeup Artists",
      "Hair Salons & Stylists",
      "Barber Shops",
      "Nail Studios",
      "Spa & Massage",
      "Beauty & Wellness Services",
      // legacy labels (kept for existing profiles)
      "Hair Stylist",
      "Barber",
      "Nail Technicians",
      "Beauty & Spa",
    ],
  },
  {
    industry: "Food & Hospitality",
    categories: [
      "Restaurants",
      "CafÃ©s & Coffee Shops",
      "Bakeries & Cake Shops",
      "Catering Services",
      "Cloud Kitchen & Food Delivery",
      "Sweet Shops & Confectionery",
      "Street Food & Quick Bites",
      "Bars & Pubs",
      "Food Trucks & Stalls",
      // legacy / broader labels (kept for existing profiles)
      "Restaurants & Fine Dining",
      "CafÃ©s & Quick Service",
      "Catering & Banquets",
      "Bakery & Confectionery",
      "Bars & Nightlife",
      "Packaged Foods",
      "Beverages & Juices",
      "Food Manufacturing",
      "Ingredients & Supply",
    ],
  },
  {
    industry: "Hotels & Stays",
    categories: [
      "Hotels & Lodging",
      "Resorts",
      "Homestays & Guest Houses",
      "Boutique Hotels",
      "Vacation Rentals",
      "Hostels & Budget Stays",
      "Service Apartments",
      // legacy labels (kept for existing profiles)
      "Hotels & Resorts",
      "Boutique & Guest Houses",
      "Vacation Rentals & Homestays",
      "Spa & Wellness Retreats",
      "Event Venues & Conferences",
    ],
  },
  {
    industry: "Photography & Videography",
    categories: [
      "Bridal Photography",
      "Wedding Cinematography",
      "Pre-Wedding & Engagement Shoots",
      "Event Photography",
      "Portrait & Studio Photography",
      "Product & Commercial Photography",
      "Videography & Reels",
      // legacy labels (kept for existing profiles)
      "Film & Video Production",
    ],
  },
  {
    industry: "Marketing & Technology",
    categories: [
      "Digital Marketing Agency",
      "Social Media Marketing",
      "SEO & Content Marketing",
      "Software Products",
      "Software Development & IT Services",
      "Web & App Development",
      "Graphic Design & Branding",
      // legacy labels (kept for existing profiles)
      "SaaS & Cloud",
      "Mobile & Web Apps",
      "IT Services & Consulting",
      "Marketing & Advertising",
      "Design & Creative",
      "Cybersecurity",
      "AI & Data",
      "Hardware & Devices",
      "Gaming & Interactive",
    ],
  },
  {
    industry: "Hospitality & Tourism",
    categories: [
      "Restaurants",
      "CafÃ©s & Coffee Shops",
      "Bakeries & Cake Shops",
      "Catering Services",
      "Hotels & Lodging",
      "Resorts",
      "Homestays & Guest Houses",
      "Travel Agencies & Tour Operators",
      // legacy labels (kept for existing profiles)
      "Hotels & Resorts",
      "Boutique & Guest Houses",
      "Vacation Rentals & Homestays",
      "Restaurants & Fine Dining",
      "CafÃ©s & Quick Service",
      "Bars & Nightlife",
      "Catering & Banquets",
      "Airlines & Aviation",
      "Cruise & Marine Tourism",
      "Destination Management",
      "Event Venues & Conferences",
      "Spa & Wellness Retreats",
    ],
  },
  {
    industry: "Retail & E-commerce",
    categories: [
      "Grocery & Daily Needs",
      "Fashion & Apparel",
      "Beauty & Personal Care",
      "Electronics & Gadgets",
      "Home & Furniture",
      "Sports & Outdoor",
      "Books & Stationery",
      "Gifts & Specialty",
      "Online Marketplace",
    ],
  },
  {
    industry: "Food & Beverage",
    categories: [
      "Bakeries & Cake Shops",
      "Sweet Shops & Confectionery",
      "Cloud Kitchen & Food Delivery",
      "Packaged Foods",
      "Beverages & Juices",
      // legacy labels (kept for existing profiles)
      "Bakery & Confectionery",
      "Cloud Kitchen & Delivery",
      "Food Manufacturing",
      "Ingredients & Supply",
    ],
  },
  {
    industry: "Healthcare & Wellness",
    categories: [
      "Clinics & Polyclinics",
      "Dental & Vision",
      "Pharmacy & Diagnostics",
      "Fitness & Gyms",
      "Nutrition & Diet",
      "Mental Health & Counseling",
      "Alternative & Holistic Care",
      // legacy labels (kept for existing profiles)
      "Hospitals & Clinics",
      "Medical Devices",
    ],
  },
  {
    industry: "Technology & Software",
    categories: [
      "Software Products",
      "Software Development & IT Services",
      "Web & App Development",
      "SaaS & Cloud",
      "Mobile & Web Apps",
      "IT Services & Consulting",
      "Cybersecurity",
      "AI & Data",
      "Hardware & Devices",
      "Gaming & Interactive",
    ],
  },
  {
    industry: "Professional Services",
    categories: [
      "Digital Marketing Agency",
      "Design & Creative",
      "Legal",
      "Accounting & Audit",
      "Management Consulting",
      "HR & Recruitment",
      "Marketing & Advertising",
      "Architecture & Engineering",
    ],
  },
  {
    industry: "Education & Training",
    categories: [
      "Schools & Kâ€“12",
      "Higher Education",
      "EdTech & Online Learning",
      "Coaching & Test Prep",
      "Corporate Training",
      "Language & Skills",
    ],
  },
  {
    industry: "Real Estate",
    categories: [
      "Residential Sales & Leasing",
      "Commercial Real Estate",
      "Property Management",
      "Construction & Development",
      "Interior Design & Staging",
    ],
  },
  {
    industry: "Financial Services",
    categories: [
      "Banking",
      "Insurance",
      "Investment & Wealth",
      "Fintech & Payments",
      "Lending & NBFC",
    ],
  },
  {
    industry: "Manufacturing & Industrial",
    categories: [
      "Automotive & Parts",
      "Textiles & Apparel Manufacturing",
      "Chemicals & Materials",
      "Machinery & Equipment",
      "Electronics Assembly",
      "Packaging",
    ],
  },
  {
    industry: "Media & Entertainment",
    categories: [
      "Bridal Photography",
      "Wedding Cinematography",
      "Film & Video Production",
      "Music & Audio",
      "Publishing & News",
      "Streaming & Digital Media",
      "Events & Experiences",
      "Influencer & Creator",
    ],
  },
  {
    industry: "Automotive & Mobility",
    categories: [
      "Dealerships",
      "Auto Parts & Accessories",
      "EV & Charging",
      "Fleet & Logistics Vehicles",
      "Two-Wheelers",
    ],
  },
  {
    industry: "Construction & Infrastructure",
    categories: [
      "General Contracting",
      "Civil & Infrastructure",
      "Electrical & MEP",
      "Interior Fit-Out",
      "Suppliers & Materials",
    ],
  },
  {
    industry: "Agriculture & Food Production",
    categories: [
      "Farming & Produce",
      "Dairy & Poultry",
      "Agri-Tech",
      "Organic & Specialty",
      "Export & Supply Chain",
    ],
  },
  {
    industry: "Energy & Utilities",
    categories: [
      "Renewable & Solar",
      "Oil & Gas",
      "Power Distribution",
      "Water & Waste",
    ],
  },
  {
    industry: "Non-profit & Public Sector",
    categories: [
      "Charities & NGOs",
      "Arts & Culture",
      "Community & Social Services",
      "Government & Civic",
    ],
  },
];

/** @type {string[]} */
export const INDUSTRIES = INDUSTRY_CATEGORY_LIST.map((item) => item.industry);

/**
 * Map: industry label â†’ list of categories (new object each call is avoided â€” use this reference).
 * @type {Record<string, string[]>}
 */
export const CATEGORIES_BY_INDUSTRY = INDUSTRY_CATEGORY_LIST.reduce((acc, { industry, categories }) => {
  acc[industry] = categories;
  return acc;
}, {});

/**
 * @param {string} industry
 * @returns {string[]}
 */
export function getCategoriesForIndustry(industry) {
  if (!industry || !CATEGORIES_BY_INDUSTRY[industry]) return [];
  return CATEGORIES_BY_INDUSTRY[industry];
}
